// The browser-side port of project-service's GradientSvgUtil (Java), which MUST stay
// byte-for-byte equivalent so an AI-placed gradient renders identically to a manually-placed one
// (see docs/plans/design3d-gradient-fills-plan.md's "Backend symmetry" section) - true for every
// call site that leaves the optional center/radius params at their defaults (0.5/0.5/0.5, this
// function's original always-centered behavior). Those params are a client-only addition for the
// interactive radial drag handles (design-3d.component.ts's attachRadialGradientControls()); the
// backend has no way to place an off-center radial yet, so this genuinely can't diverge from it
// until GradientSvgUtil grows the same params - flag that as follow-up work, not a bug here.
//
// Deliberately not a live Fabric.js Gradient assigned to an object's fill - the print engine's own
// DesignObject.fill (builder-cb) is a plain string, so a structured fill value there fails to
// deserialize. Baked to a flat image instead, same as geometric-pattern.util.ts: generated at
// exactly the target pixel size, so the caller places it the same simple way it already places any
// other image.

export type GradientType = 'LINEAR' | 'RADIAL';

/**
 * @param type "LINEAR" or "RADIAL" (case-insensitive; anything else falls back to LINEAR)
 * @param fromColor the gradient's start color
 * @param toColor the gradient's end color
 * @param angleDegrees direction for a LINEAR gradient, matching Fabric's own clockwise-positive
 *   angle convention (0 = left-to-right); ignored for RADIAL
 * @param width the exact SVG width to generate, in pixels
 * @param height the exact SVG height to generate, in pixels
 * @param centerXPercent RADIAL only, 0-1 fraction of width; defaults to 0.5 (centered), matching
 *   this function's original always-centered behavior when omitted - see the interactive drag
 *   handles in design-3d.component.ts's attachRadialGradientControls()
 * @param centerYPercent RADIAL only, 0-1 fraction of height; defaults to 0.5
 * @param radiusPercent RADIAL only, 0-1+ fraction of width; defaults to 0.5
 */
export function svgGradient(
  type: string,
  fromColor: string,
  toColor: string,
  angleDegrees: number,
  width: number,
  height: number,
  centerXPercent = 0.5,
  centerYPercent = 0.5,
  radiusPercent = 0.5,
): string {
  const radial = type?.toUpperCase() === 'RADIAL';
  const fillDefinition = radial
    ? `<radialGradient id="g" cx="${centerXPercent * 100}%" cy="${centerYPercent * 100}%" r="${radiusPercent * 100}%">` +
      `<stop offset="0%" stop-color="${fromColor}"/>` +
      `<stop offset="100%" stop-color="${toColor}"/>` +
      `</radialGradient>`
    : `<linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="0%" gradientTransform="rotate(${angleDegrees} 0.5 0.5)">` +
      `<stop offset="0%" stop-color="${fromColor}"/>` +
      `<stop offset="100%" stop-color="${toColor}"/>` +
      `</linearGradient>`;

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">` +
    `<defs>${fillDefinition}</defs>` +
    `<rect width="100%" height="100%" fill="url(#g)"/>` +
    `</svg>`
  );
}
